import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Combobox, type ComboItem } from '@/components/combobox';
import { ErrorBanner } from '@/components/form';
import { FileDrop, StatusChip } from '@/components/migration/file-drop';
import { GetFromQuickBooks } from '@/components/migration/quickbooks';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAccounts } from '@/lib/accounts';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatDate } from '@/lib/format';
import {
  fmtAmount,
  mappedTotals,
  readUpload,
  useMigration,
  type MigrationView,
  type TrialBalanceLine,
  type TrialBalancePreview,
} from '@/lib/migration';
import { formatMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings/migration/opening-balances')({
  component: OpeningBalancesPage,
});

type Step = 'upload' | 'map' | 'review' | 'done';
const STEPS: Array<{ key: Step; label: string }> = [
  { key: 'upload', label: 'Upload' },
  { key: 'map', label: 'Map accounts' },
  { key: 'review', label: 'Review' },
  { key: 'done', label: 'Done' },
];
const HOW: Record<NonNullable<TrialBalanceLine['how']>, string> = {
  saved: 'Saved',
  number: 'Number',
  path: 'Path',
  name: 'Name',
};

/** Screen G8 (document 06): the opening trial balance from QuickBooks. */
function OpeningBalancesPage() {
  const orgId = useOrgId();
  const me = useMe();
  const m = useMigration();
  return (
    <>
      <PageHeader
        title="Opening balances"
        description="Import the QuickBooks trial balance as of the day before the conversion date and map each account to this chart of accounts."
        actions={
          <Button variant="outline" asChild>
            <Link to="/settings/migration">Move from QuickBooks</Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : me.data && !me.data.isStaff ? (
        <EmptyState title="Only accounting staff can import opening balances" />
      ) : m.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : m.isError ? (
        <EmptyState title="Could not load the migration" hint={m.error.message} />
      ) : !m.data.conversionDate ? (
        <EmptyState
          title="Set the conversion date first"
          hint="Opening balances are dated the day before the conversion date."
          action={
            <Button asChild>
              <Link to="/settings/migration">Move from QuickBooks</Link>
            </Button>
          }
        />
      ) : m.data.status === 'finished' ? (
        <EmptyState
          title="The move from QuickBooks is finished"
          hint="The books are locked through the opening date, so opening balances no longer change."
        />
      ) : (
        <Wizard key={orgId} view={m.data} />
      )}
    </>
  );
}

function Wizard({ view }: { view: MigrationView }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const accounts = useAccounts();
  const [step, setStep] = useState<Step>('upload');
  const [fileError, setFileError] = useState<string | null>(null);
  const [preview, setPreview] = useState<TrialBalancePreview | null>(null);
  const [lines, setLines] = useState<TrialBalanceLine[]>([]);
  const [posted, setPosted] = useState<MigrationView | null>(null);

  const byId = useMemo(() => new Map((accounts.data ?? []).map((a) => [a.id, a])), [accounts.data]);
  const roleOf = (id: string | null): TrialBalanceLine['role'] => {
    const t = id ? byId.get(id)?.detailType : undefined;
    return t === 'accounts_receivable'
      ? 'receivable'
      : t === 'accounts_payable'
        ? 'payable'
        : 'post';
  };
  const items: ComboItem[] = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => a.isActive)
        .map((a) => ({
          id: a.id,
          label: `${a.code} ${a.fullName}`,
          keywords: a.accountTypeName,
          group: a.accountTypeName,
        })),
    [accounts.data],
  );

  const read = useMutation({
    mutationFn: async (f: File) => {
      const file = await readUpload(f);
      return api<TrialBalancePreview>(`/orgs/${orgId}/migration/trial-balance/preview`, {
        method: 'POST',
        body: JSON.stringify({ file }),
      });
    },
    onMutate: () => setFileError(null),
    onSuccess: (p) => {
      setPreview(p);
      setLines(p.lines);
      setStep('map');
    },
    onError: (e) => setFileError((e as Error).message),
  });

  const post = useMutation({
    mutationFn: () =>
      api<MigrationView>(`/orgs/${orgId}/migration/trial-balance`, {
        method: 'POST',
        body: JSON.stringify({
          fileName: preview!.fileName,
          asOf: preview!.asOf,
          lines: lines.map((l) => ({
            label: l.label,
            debit: l.debit,
            credit: l.credit,
            accountId: l.accountId,
          })),
        }),
      }),
    onSuccess: async (v) => {
      setPosted(v);
      setStep('done');
      qc.setQueryData(['migration', orgId], v);
      await qc.invalidateQueries({ queryKey: ['accounts', orgId] });
    },
  });

  const totals = mappedTotals(lines, roleOf);
  const balanced = totals.debit === totals.credit;
  const at = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="flex max-w-6xl flex-col gap-4">
      <ol className="flex items-center gap-2 text-sm" aria-label="Import steps">
        {STEPS.map((s, i) => (
          <li key={s.key} className="flex items-center gap-2">
            <span
              className={cn(
                'flex size-6 items-center justify-center rounded-full border text-xs',
                i < at && 'border-primary bg-primary text-primary-foreground',
                i === at && 'border-primary text-primary',
                i > at && 'text-muted-foreground',
              )}
            >
              {i < at ? <Check className="size-3.5" /> : i + 1}
            </span>
            <span
              className={cn(i === at ? 'font-medium' : 'text-muted-foreground')}
              aria-current={i === at ? 'step' : undefined}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 ? <span className="mx-1 h-px w-8 bg-border" /> : null}
          </li>
        ))}
      </ol>

      {step === 'upload' ? (
        <>
          {view.trialBalance ? (
            <div className="rounded-lg border bg-card px-4 py-3 text-sm" role="status">
              Opening balances are posted as journal no.{' '}
              <Link
                to="/accounting/journal-entries/$entryId"
                params={{ entryId: view.trialBalance.entryId }}
                className="text-primary hover:underline"
              >
                {view.trialBalance.entryNo}
              </Link>
              {view.trialBalance.fileName ? ` from ${view.trialBalance.fileName}` : ''}. Importing
              another file reverses that entry and posts the new one.
            </div>
          ) : null}
          <ErrorBanner>{fileError}</ErrorBanner>
          <FileDrop
            title="Drag and drop the QuickBooks trial balance here, or browse"
            hint={
              <>
                Reports › Trial balance, accrual basis, as of{' '}
                <strong>{formatDate(view.openingDate!)}</strong>, exported to Excel (.xlsx) or CSV.
                Leave the title rows in.
              </>
            }
            busy={read.isPending}
            onFile={(f) => read.mutate(f)}
          />
          <GetFromQuickBooks kind="trial-balance" onFile={(f) => read.mutate(f)} />
        </>
      ) : null}

      {step === 'map' && preview ? (
        <>
          <ErrorBanner>
            {preview.issues.length
              ? preview.issues
                  .map((i) => (i.row ? `Row ${i.row}: ${i.message}` : i.message))
                  .join(' · ')
              : null}
          </ErrorBanner>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{preview.fileName}</span>
            {preview.asOf ? ` · as of ${formatDate(preview.asOf)}` : ''} · {lines.length} accounts.
            {preview.asOf && preview.asOf !== view.openingDate ? (
              <span className="text-warning">
                {' '}
                The file is not as of {formatDate(view.openingDate!)}, the day before the conversion
                date.
              </span>
            ) : null}{' '}
            Accounts were matched by a saved mapping, number, sub-account path or name; check the
            rest. A/R and A/P come in through the open invoices and unpaid bills.
          </p>
          <div className="max-h-[560px] overflow-y-auto rounded-lg border bg-card">
            <Table>
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead>QuickBooks account</TableHead>
                  <TableHead className="w-[130px] text-right">Debit</TableHead>
                  <TableHead className="w-[130px] text-right">Credit</TableHead>
                  <TableHead className="w-[340px]">Yedo Books account</TableHead>
                  <TableHead className="w-[140px]">Matched by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l, i) => {
                  const role = roleOf(l.accountId);
                  return (
                    <TableRow key={`${l.row}-${l.label}`}>
                      <TableCell className="font-medium">
                        {l.label}
                        {role !== 'post' ? (
                          <div className="text-xs font-normal text-muted-foreground">
                            Comes in through{' '}
                            {role === 'receivable' ? 'open invoices' : 'unpaid bills'}; not posted
                            here
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="tnum text-right">{fmtAmount(l.debit, true)}</TableCell>
                      <TableCell className="tnum text-right">{fmtAmount(l.credit, true)}</TableCell>
                      <TableCell>
                        <Combobox
                          items={items}
                          value={l.accountId}
                          ariaLabel={`Account for ${l.label}`}
                          invalid={!l.accountId}
                          placeholder="Choose an account"
                          onChange={(id) =>
                            setLines((prev) =>
                              prev.map((x, j) =>
                                j === i ? { ...x, accountId: id, how: null } : x,
                              ),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {l.how ? (
                          <StatusChip tone="success">{HOW[l.how]}</StatusChip>
                        ) : l.accountId ? (
                          <StatusChip tone="info">Chosen</StatusChip>
                        ) : (
                          <StatusChip tone="warning">Not matched</StatusChip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-semibold">Total</TableCell>
                  <TableCell className="tnum text-right font-semibold">
                    {formatMinor(totals.debit)}
                  </TableCell>
                  <TableCell className="tnum text-right font-semibold">
                    {formatMinor(totals.credit)}
                  </TableCell>
                  <TableCell colSpan={2} className="text-sm">
                    {!balanced
                      ? `Out of balance by ${formatMinor(totals.debit - totals.credit)}`
                      : totals.unmapped
                        ? `${totals.unmapped} not mapped`
                        : 'Balanced; every account mapped'}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('upload')}>
              Back
            </Button>
            <Button
              disabled={!balanced || totals.unmapped > 0 || lines.length < 2}
              onClick={() => setStep('review')}
            >
              Next
            </Button>
          </div>
        </>
      ) : null}

      {step === 'review' && preview ? (
        <>
          <ErrorBanner>{post.error?.message}</ErrorBanner>
          <div className="rounded-lg border bg-card px-4 py-3 text-sm" role="status">
            One opening-balance entry dated <strong>{formatDate(view.openingDate!)}</strong> will
            post {lines.filter((l) => roleOf(l.accountId) === 'post').length} balances. A/R less
            A/P, <strong className="tnum">{formatMinor(totals.awaiting)}</strong>, waits in Opening
            balance equity until the open invoices and unpaid bills are imported; then it is zero.
            {view.trialBalance ? ' The earlier opening entry is reversed.' : ''}
          </div>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead className="w-[140px] text-right">Debit</TableHead>
                  <TableHead className="w-[140px] text-right">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines
                  .filter((l) => roleOf(l.accountId) === 'post')
                  .map((l) => {
                    const a = byId.get(l.accountId!);
                    return (
                      <TableRow key={`${l.row}-${l.label}`}>
                        <TableCell>{a ? `${a.code} ${a.fullName}` : l.label}</TableCell>
                        <TableCell className="tnum text-right">
                          {fmtAmount(l.debit, true)}
                        </TableCell>
                        <TableCell className="tnum text-right">
                          {fmtAmount(l.credit, true)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('map')}>
              Back
            </Button>
            <Button disabled={post.isPending} onClick={() => post.mutate()}>
              {post.isPending ? 'Posting…' : 'Post opening balances'}
            </Button>
          </div>
        </>
      ) : null}

      {step === 'done' && posted?.trialBalance ? (
        <>
          <div
            className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm"
            role="status"
          >
            <span className="font-medium">Opening balances posted</span> as journal no.{' '}
            <Link
              to="/accounting/journal-entries/$entryId"
              params={{ entryId: posted.trialBalance.entryId }}
              className="text-primary hover:underline"
            >
              {posted.trialBalance.entryNo}
            </Link>
            . The account mapping is saved for this client. Next, import the open invoices and
            unpaid bills.
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" asChild>
              <Link to="/settings/migration">Back to the checklist</Link>
            </Button>
            <Button asChild>
              <Link to="/settings/migration/open-items/$side" params={{ side: 'receivable' }}>
                Import open invoices
              </Link>
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
